import api from './axiosConfig';
import { parametricasService } from './parametricasService';

// ── Tipos de ID flexibles ──────────────────────────────────────
type ID = number | string;

// ── Secciones ─────────────────────────────────────────────────
export const seccionesService = {
  // Acepta número (empresaId) o boolean (soloActivas → usa empresaId=0)
  getAll:  (empresaIdOrBool: number | boolean = 0) => {
    const empresaId = typeof empresaIdOrBool === 'boolean' ? 0 : empresaIdOrBool;
    return api.get('/pos/catalogo/secciones', { params: { empresaId } }).then(r => r.data);
  },
  create:  (data: any) => api.post('/pos/catalogo/secciones', data).then(r => r.data),
  update:  (id: ID, data: any) => api.put(`/pos/catalogo/secciones/${id}`, data).then(r => r.data),
  delete:  (id: ID) => api.delete(`/pos/catalogo/secciones/${id}`).then(r => r.data),
};

// ── Categorías ────────────────────────────────────────────────
export const categoriasService = {
  getAll:       (empresaId: ID = 0, seccionId?: ID) =>
    api.get('/pos/catalogo/categorias', { params: { empresaId, seccionId } }).then(r => r.data),
  getBySeccion: (seccionId: ID, empresaId: ID = 0) =>
    api.get('/pos/catalogo/categorias', { params: { empresaId, seccionId } }).then(r => r.data),
  create:       (data: any) => api.post('/pos/catalogo/categorias', data).then(r => r.data),
  update:       (id: ID, data: any) => api.put(`/pos/catalogo/categorias/${id}`, data).then(r => r.data),
  delete:       (id: ID) => api.delete(`/pos/catalogo/categorias/${id}`).then(r => r.data),
};

// ── Subcategorías (categorías con padre) ──────────────────────
export const subcategoriasService = {
  getByCategoria: (categoriaId: ID, empresaId: ID = 0) =>
    api.get('/pos/catalogo/categorias', { params: { empresaId } })
      .then(r => (r.data?.data ?? r.data ?? []).filter((c: any) => c.categoriaPadreId === Number(categoriaId))),
  getAll:  (categoriaId?: ID): Promise<any[]> => Promise.resolve([]),
  create:  (data: any) => api.post('/pos/catalogo/categorias', data).then(r => r.data),
  update:  (id: ID, data: any) => api.put(`/pos/catalogo/categorias/${id}`, data).then(r => r.data),
  delete:  (id: ID) => api.delete(`/pos/catalogo/categorias/${id}`).then(r => r.data),
};

// ── Familias (no existe en backend POS) ───────────────────────
export const familiasService = {
  getAll:   (_subcategoriaId?: ID): Promise<any[]> => Promise.resolve([]),
  create:   (_data: any): Promise<any>             => Promise.resolve({}),
  update:   (_id: ID, _data: any): Promise<any>    => Promise.resolve({}),
  delete:   (_id: ID): Promise<void>               => Promise.resolve(),
};

// ── Marcas ────────────────────────────────────────────────────
export const marcasService = {
  getAll:  (empresaId: ID = 0) =>
    api.get('/pos/catalogo/marcas', { params: { empresaId } }).then(r => r.data),
  create:  (data: any) => api.post('/pos/catalogo/marcas', data).then(r => r.data),
  update:  (id: ID, data: any) => api.put(`/pos/catalogo/marcas/${id}`, data).then(r => r.data),
};

// ── Proveedores ───────────────────────────────────────────────
export const proveedoresService = {
  getAll:  (empresaId: ID = 0, buscar?: string, page = 1, pageSize = 20) =>
    api.get('/pos/catalogo/proveedores', { params: { empresaId, buscar, page, pageSize } }).then(r => r.data),
  getById: (id: ID) => api.get(`/pos/catalogo/proveedores/${id}`).then(r => r.data),
  create:  (data: any) => api.post('/pos/catalogo/proveedores', data).then(r => r.data),
  update:  (id: ID, data: any) => api.put(`/pos/catalogo/proveedores/${id}`, data).then(r => r.data),
  delete:  (id: ID) =>
    api.patch(`/pos/catalogo/proveedores/${id}/estado`, false).then(r => r.data),
};

// ── Productos ─────────────────────────────────────────────────
export const productosService = {
  // Acepta (empresaId, params?) o (filtros: objeto)
  getAll: (empresaIdOrFiltros?: ID | Record<string, any>, params?: Record<string, any>) => {
    if (typeof empresaIdOrFiltros === 'number') {
      return api.get('/pos/catalogo/productos', {
        params: { empresaId: empresaIdOrFiltros, ...params },
      }).then(r => r.data);
    }
    const f = (empresaIdOrFiltros as Record<string, any>) ?? {};
    return api.get('/pos/catalogo/productos', {
      params: { empresaId: f.empresaId ?? 0, categoriaId: f.categoriaId, soloActivos: f.soloActivos, page: f.page, pageSize: f.pageSize },
    }).then(r => r.data);
  },
  getById:      (id: ID) => api.get(`/pos/catalogo/productos/${id}`).then(r => r.data),
  // Acepta (empresaId, q) o (termino)
  buscar: (empresaIdOrTermino: ID, q?: string) => {
    if (typeof empresaIdOrTermino === 'number' && q !== undefined) {
      return api.get('/pos/catalogo/productos/buscar', { params: { empresaId: empresaIdOrTermino, q } }).then(r => r.data);
    }
    return api.get('/pos/catalogo/productos/buscar', {
      params: { empresaId: 0, q: String(empresaIdOrTermino) },
    }).then(r => r.data);
  },
  create:       (data: any) => api.post('/pos/catalogo/productos', data).then(r => r.data),
  update:       (id: ID, data: any) => api.put(`/pos/catalogo/productos/${id}`, data).then(r => r.data),
  delete:       (id: ID) => api.patch(`/pos/catalogo/productos/${id}/estado`, false).then(r => r.data),
  toggleActivo: (id: ID) => api.patch(`/pos/catalogo/productos/${id}/estado`, true).then(r => r.data),
  cambiarEstado:(id: ID, activo: boolean) => api.patch(`/pos/catalogo/productos/${id}/estado`, activo).then(r => r.data),
  duplicar:     (id: ID): Promise<any> =>
    api.get(`/pos/catalogo/productos/${id}`)
      .then(r => ({ ...r.data, productoId: 0, codigo: r.data?.codigo + '_COPIA' })),
};

// ── Unidades de Medida ────────────────────────────────────────
export const unidadesMedidaService = {
  getAll:  () => parametricasService.getUnidadesMedida(),
  getById: (id: ID) =>
    parametricasService.getUnidadesMedida()
      .then((data: any[]) => data.find((u: any) => u.unidadMedidaId === Number(id))),
};

// ── Impuestos / Tarifas IVA ───────────────────────────────────
export const impuestosService = {
  getAll:  () => parametricasService.getTarifasIVA(),
  getById: (id: ID) =>
    parametricasService.getTarifasIVA()
      .then((data: any[]) => data.find((t: any) => t.tarifaIVAId === Number(id))),
};
